import { ShipmentsClient, useShipmentsClient } from '@frontend/clients/shipments.request'
import { useClearBookingsCache } from '@frontend/queries/bookings'
import { useClearFacilityRampsCache } from '@frontend/queries/companies'
import { useClearAllPurchaseOrdersCache } from '@frontend/queries/purchase-order'
import {
  useClearAllShipmentDetailsCache,
  useClearShipmentSummariesCache,
  useRefetchShipmentDetails,
} from '@frontend/queries/shipments'
import { useMutation } from '@tanstack/react-query'

import { BulkShipmentsUpdate } from '@shared-types/bulk_actions'
import {
  AttachmentV2CreatePayload,
  ReplacePlannedShippingUnitsPayload,
  ReplaceReceivedShippingUnitsPayload,
  RestrictedShipmentUpdatePayload,
  ShipmentAnomaliesUpdatePayload,
  ShipmentPurchaseOrderCreationPayload,
  ShipmentPurchaseOrderStatusUpdatePayload,
  ShipmentStatusUpdatePayload,
  ShipmentUpdatePayload,
  ShipmentUpdateRampPayload,
  ShipmentV2CreationPayload,
  SMSNotificationPayload,
  SPOReplacePayload,
} from '@shared-types/shipments'
import { Identifier, NanoId } from '@shared-types/util'

import { useClearCompaniesCache } from './companies'
import { useClearTransportationCache } from './transportation'

/*
 * A helper hook to clear any query caches when we update or create shipments
 */
const useShipmentsClientRequest = () => {
  const shipmentsClient = useShipmentsClient()
  const clearBookingsCache = useClearBookingsCache()
  const clearShipmentSummariesCache = useClearShipmentSummariesCache()
  const clearPurchaseOrdersCache = useClearAllPurchaseOrdersCache()
  const clearFacilityRampsCache = useClearFacilityRampsCache()
  const clearCompaniesCache = useClearCompaniesCache()
  const clearTransportationCache = useClearTransportationCache()

  return async <T>(func: (shipmentsClient: ShipmentsClient) => Promise<T>): Promise<T> => {
    const result = await func(shipmentsClient)
    await clearBookingsCache()
    await clearShipmentSummariesCache()
    await clearPurchaseOrdersCache()
    await clearFacilityRampsCache()
    await clearCompaniesCache()
    await clearTransportationCache()
    return result
  }
}

type UseMutateCreateShipmentV2 = {
  payload: ShipmentV2CreationPayload
}
export const useMutateCreateShipmentV2 = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()

  return useMutation({
    mutationFn: ({ payload }: UseMutateCreateShipmentV2) =>
      shipmentsClientRequest((client) => client.createV2(payload)),
  })
}

type UseMutateReplacePlannedShippingUnits = {
  nid: NanoId
  payload: ReplacePlannedShippingUnitsPayload
}
export const useMutateReplacePlannedShippingUnits = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid, payload }: UseMutateReplacePlannedShippingUnits) => {
      const result = await shipmentsClientRequest((client) =>
        client.replacePlannedShippingUnits(nid, payload)
      )
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateReplaceReceivedShippingUnits = {
  nid: NanoId
  payload: ReplaceReceivedShippingUnitsPayload
}
export const useMutateReplaceReceivedShippingUnits = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async (data: UseMutateReplaceReceivedShippingUnits) => {
      const result = await shipmentsClientRequest((client) =>
        client.replaceReceivedShippingUnits(data.nid, data.payload)
      )
      await refetchShipmentDetails(data.nid)
      return result
    },
  })
}

type UseMutatePatchShipment = {
  shipmentNid: NanoId
  payload: ShipmentUpdatePayload
}
export const useMutatePatchShipment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()
  return useMutation({
    mutationFn: async ({ shipmentNid, payload }: UseMutatePatchShipment) => {
      const result = await shipmentsClientRequest((client) => client.update(shipmentNid, payload))
      await refetchShipmentDetails(shipmentNid)
      return result
    },
  })
}

type UseMutateResetShipment = {
  shipmentNid: NanoId
}
export const useMutateResetShipmentCheckIn = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ shipmentNid }: UseMutateResetShipment) => {
      const result = await shipmentsClientRequest((client) => client.resetCheckIn(shipmentNid))
      void refetchShipmentDetails(shipmentNid)
      return result
    },
  })
}

type UseMutateUpdateShipmentStatus = {
  shipmentNid: NanoId
  payload: ShipmentStatusUpdatePayload
}
export const useMutateShipmentStatus = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()
  return useMutation({
    mutationFn: async ({ shipmentNid, payload }: UseMutateUpdateShipmentStatus) => {
      const result = await shipmentsClientRequest((client) =>
        client.updateStatus(shipmentNid, payload)
      )
      await clearAllShipmentDetailsCache()
      return result
    },
  })
}

type UseMutateReplaceSPOs = {
  nid: NanoId
  payload: SPOReplacePayload
}
export const useMutateReplaceSPOs = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid, payload }: UseMutateReplaceSPOs) => {
      const result = await shipmentsClientRequest((client) =>
        client.replaceShipmentPurchaseOrders(nid, payload)
      )
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateArchiveShipment = {
  nid: NanoId
}
export const useMutateArchiveShipment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid }: UseMutateArchiveShipment) => {
      const result = await shipmentsClientRequest((client) => client.archive(nid))
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

export const useMutateRestoreShipment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid }: UseMutateArchiveShipment) => {
      const result = await shipmentsClientRequest((client) => client.restore(nid))
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateGenerateShipmentManifest = {
  receivingCompanyId: Identifier
  payload: FormData
}
export const useMutateGenerateShipmentManifest = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()

  return useMutation({
    mutationFn: ({ receivingCompanyId, payload }: UseMutateGenerateShipmentManifest) =>
      shipmentsClientRequest((client) => {
        return client.generateShipmentManifestPOs(receivingCompanyId, payload)
      }),
  })
}

type UseMutateGetShipmentSecretToken = {
  shipmentNid: NanoId
}
export const useMutateGetShipmentSecretToken = () => {
  const shipmentsClient = useShipmentsClient()

  return useMutation({
    mutationFn: ({ shipmentNid }: UseMutateGetShipmentSecretToken) =>
      shipmentsClient.getShipmentSecretToken(shipmentNid),
  })
}

type UseMutateUpdateShipmentPurchaseOrderStatus = {
  nid: NanoId
  purchaseOrderId: Identifier
  payload: ShipmentPurchaseOrderStatusUpdatePayload
}
export const useMutateUpdateShipmentPurchaseOrderStatus = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()
  return useMutation({
    mutationFn: async ({
      nid,
      purchaseOrderId,
      payload,
    }: UseMutateUpdateShipmentPurchaseOrderStatus) => {
      const result = await shipmentsClientRequest((client) =>
        client.updateSPOStatus(nid, purchaseOrderId, payload)
      )
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateReplaceShipmentPurchaseOrder = {
  nid: NanoId
  purchaseOrderId: string
  payload: ShipmentPurchaseOrderCreationPayload
}
export const useMutateReplaceShipmentPurchaseOrder = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({
      nid,
      purchaseOrderId,
      payload,
    }: UseMutateReplaceShipmentPurchaseOrder) => {
      const result = await shipmentsClientRequest((client) =>
        client.replaceShipmentPurchaseOrder(nid, purchaseOrderId, payload)
      )
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateDeleteShipmentPurchaseOrder = {
  nid: NanoId
  purchaseOrderId: string
}
export const useMutateDeleteShipmentPurchaseOrder = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid, purchaseOrderId }: UseMutateDeleteShipmentPurchaseOrder) => {
      const result = await shipmentsClientRequest((client) =>
        client.deleteShipmentPurchaseOrder(nid, purchaseOrderId)
      )
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

type UseMutateUpdateShipmentUsingToken = {
  token: string
  payload: RestrictedShipmentUpdatePayload
}
export const useMutateUpdateShipmentUsingToken = () => {
  const bookingsClientRequest = useShipmentsClientRequest()

  return useMutation({
    mutationFn: (data: UseMutateUpdateShipmentUsingToken) =>
      bookingsClientRequest((client) => client.restrictedUpdate(data.token, data.payload)),
  })
}

type UseMutateAddFileAttachmentV2 = {
  nid: NanoId
  attachments: AttachmentV2CreatePayload[]
}
export const useMutateAddShipmentFileAttachment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  return useMutation({
    mutationFn: (data: UseMutateAddFileAttachmentV2) =>
      shipmentsClientRequest((client) => client.addFileAttachmentsV2(data.nid, data.attachments)),
  })
}

type UseMutateDeleteFileAttachmentV2 = {
  nid: NanoId
  attachmentId: Identifier
}
export const useMutateDeleteShipmentFileAttachment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  return useMutation({
    mutationFn: (data: UseMutateDeleteFileAttachmentV2) =>
      shipmentsClientRequest((client) =>
        client.deleteFileAttachmentV2(data.nid, data.attachmentId)
      ),
  })
}

type UseMutateAssignShipmentRamp = {
  facilityId: Identifier
  nid: NanoId
  payload: ShipmentUpdateRampPayload
}

export const useMutateAssignShipmentRamp = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()

  return useMutation({
    mutationFn: async ({ facilityId, nid, payload }: UseMutateAssignShipmentRamp) => {
      const result = await shipmentsClientRequest((client) =>
        client.assignRamp(facilityId, nid, payload)
      )
      await clearAllShipmentDetailsCache()
      return result
    },
  })
}

type useMutateSmsNotification = {
  nid: NanoId
  payload: SMSNotificationPayload
}
export const useMutateSmsNotifications = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid, payload }: useMutateSmsNotification) => {
      const result = await shipmentsClientRequest((client) => client.smsNotifications(nid, payload))
      await refetchShipmentDetails(nid)
      return result
    },
  })
}

export const useMutateBulkUpdateShipments = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  return useMutation({
    mutationFn: (payload: BulkShipmentsUpdate) =>
      shipmentsClientRequest((client) => client.bulkUpdate(payload)),
  })
}

type UseMutateDeleteShipment = {
  nid: NanoId
}
export const useMutateDeleteShipment = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()

  return useMutation({
    mutationFn: async ({ nid }: UseMutateDeleteShipment) => {
      return shipmentsClientRequest((client) => client.delete(nid))
    },
  })
}

type UseMutateUpdateShipmentAnomalies = {
  nid: NanoId
  payload: ShipmentAnomaliesUpdatePayload
}
export const useMutateUpdateShipmentAnomalies = () => {
  const shipmentsClientRequest = useShipmentsClientRequest()
  const refetchShipmentDetails = useRefetchShipmentDetails()

  return useMutation({
    mutationFn: async ({ nid, payload }: UseMutateUpdateShipmentAnomalies) => {
      const result = await shipmentsClientRequest((client) => client.updateAnomalies(nid, payload))
      await refetchShipmentDetails(nid)
      return result
    },
  })
}
